import { useEffect, useState } from "react";
import { Loader2, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const EMPTY = { title: "", lang: "fr", excerpt: "", cover_url: "", content: "", published: false };
const fieldCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-navy outline-none transition-colors focus:border-azure focus:ring-2 focus:ring-azure/15";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-navy/50";

export const AdminArticles = () => {
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/articles").then((r) => setArticles(r.data)).catch(() => setArticles([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (editing.title.trim().length < 4) return toast.error("Titre trop court.");
    if (editing.content.trim().length < 10) return toast.error("Contenu trop court.");
    setSaving(true);
    try {
      if (editing.id) {
        await api.put(`/admin/articles/${editing.id}`, editing);
        toast.success("Article mis à jour.");
      } else {
        await api.post("/admin/articles", editing);
        toast.success("Article créé.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article définitivement ?")) return;
    await api.delete(`/admin/articles/${id}`).catch(() => {});
    toast.success("Article supprimé.");
    load();
  };

  if (editing) {
    return (
      <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm sm:p-8" data-testid="article-editor">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl font-semibold text-navy">{editing.id ? "Modifier l'article" : "Nouvel article"}</h2>
          <button onClick={() => setEditing(null)} data-testid="article-editor-close" className="rounded-full p-2 text-navy/50 hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className={labelCls}>Titre (pensez aux mots-clés Google)</label>
            <input className={fieldCls} data-testid="article-title-input" value={editing.title}
              onChange={(e) => setEditing((s) => ({ ...s, title: e.target.value }))}
              placeholder="ex. Transfert aéroport Nice → Monaco : prix et durée" />
          </div>
          <div>
            <label className={labelCls}>Langue</label>
            <select className={fieldCls} data-testid="article-lang-select" value={editing.lang}
              onChange={(e) => setEditing((s) => ({ ...s, lang: e.target.value }))}>
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Image de couverture — URL (optionnel)</label>
            <input className={fieldCls} data-testid="article-cover-input" value={editing.cover_url}
              onChange={(e) => setEditing((s) => ({ ...s, cover_url: e.target.value }))} placeholder="https://…" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Résumé (affiché dans Google — 150 caractères idéalement)</label>
            <textarea className={`${fieldCls} resize-none`} rows={2} data-testid="article-excerpt-input" value={editing.excerpt}
              onChange={(e) => setEditing((s) => ({ ...s, excerpt: e.target.value }))} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Contenu — ## titre, ### sous-titre, - liste, **gras**, [lien](/reservation ou https://…), ![description image](https://…)</label>
            <textarea className={`${fieldCls} resize-y font-mono text-xs`} rows={14} data-testid="article-content-input" value={editing.content}
              onChange={(e) => setEditing((s) => ({ ...s, content: e.target.value }))}
              placeholder={"Intro…\n\n## Premier conseil\n\nTexte…"} />
          </div>
          <label className="flex cursor-pointer items-center gap-3" data-testid="article-published-label">
            <input type="checkbox" data-testid="article-published-checkbox" checked={editing.published}
              onChange={(e) => setEditing((s) => ({ ...s, published: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-azure focus:ring-azure" />
            <span className="text-sm font-semibold text-navy/70">Publier immédiatement (visible sur le site et dans Google)</span>
          </label>
        </div>

        <div className="mt-6 flex gap-3">
          <button onClick={save} disabled={saving} data-testid="article-save-button"
            className="flex items-center gap-2 rounded-full bg-azure px-8 py-3 text-sm font-bold text-white transition-colors hover:bg-azure-hover disabled:opacity-60">
            {saving && <Loader2 size={15} className="animate-spin" />} Enregistrer
          </button>
          <button onClick={() => setEditing(null)} className="rounded-full border border-slate-200 px-6 py-3 text-sm font-bold text-navy/60 hover:bg-slate-50">
            Annuler
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6" data-testid="admin-articles-list">
      <button onClick={() => setEditing({ ...EMPTY })} data-testid="article-new-button"
        className="mb-5 flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-azure">
        <Plus size={16} /> Nouvel article
      </button>

      {articles === null ? (
        <Loader2 size={24} className="animate-spin text-azure" />
      ) : articles.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-navy/50">
          Aucun article. Rédigez des guides (prix des transferts, conseils aéroport…) pour remonter dans Google.
        </p>
      ) : (
        <div className="space-y-3">
          {articles.map((a) => (
            <div key={a.id} data-testid={`admin-article-${a.id.slice(0, 8)}`}
              className="flex flex-col gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm sm:flex-row sm:items-center">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${a.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {a.published ? "Publié" : "Brouillon"}
                  </span>
                  <span className="rounded-full bg-azure-soft px-3 py-1 text-xs font-bold text-azure">{a.lang.toUpperCase()}</span>
                  <span className="text-xs text-navy/40">{(a.created_at || "").slice(0, 10)}</span>
                </div>
                <p className="mt-2 font-serif text-lg font-semibold text-navy">{a.title}</p>
                <p className="text-xs text-navy/45">/blog/{a.slug}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                {a.published && (
                  <a href={`/blog/${a.slug}`} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-navy/60 hover:bg-slate-50">
                    <Star size={13} /> Voir
                  </a>
                )}
                <button onClick={() => setEditing(a)} data-testid={`article-edit-${a.id.slice(0, 8)}`}
                  className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-navy/60 hover:bg-slate-50">
                  <Pencil size={13} /> Modifier
                </button>
                <button onClick={() => remove(a.id)} data-testid={`article-delete-${a.id.slice(0, 8)}`}
                  className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-500 hover:bg-red-50">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
